'use client'

import dynamic from 'next/dynamic'
import { useState } from 'react'
import { localizeInternalHref } from '@/lib/i18n/paths'
import testimonialsData from '@/data/testimonials.json'

const FormModal = dynamic(() => import('@/components/form'), {
  ssr: false,
})

type HeroLocale = 'es' | 'en'

type HeroServiceLink = {
  label: string
  description: string
  href: string
}

type HeroCopy = {
  services: [HeroServiceLink, HeroServiceLink, HeroServiceLink, HeroServiceLink]
  servicesAriaLabel: string
  breakingBadge: string
  breakingHeadline: string
  headingLine1: string
  headingLine2: string
  intro: string
  bullets: [string, string, string]
  trustBadges: [string, string, string]
  satisfactionLabel: string
  reviewsWord: string
  primaryCta: string
  secondaryCta: string
  aiCta: string
  asesoriaCta: string
  footnote: string
}

const HERO_COPY: Record<HeroLocale, HeroCopy> = {
  es: {
    services: [
      { label: 'Nómada digital', description: 'Visado para teletrabajar desde España', href: '/visado-nomada-digital' },
      { label: 'Ley Beckham', description: 'Tributa al 24% como impatriado', href: '/ley-beckham' },
      { label: 'Residencia no lucrativa', description: 'Vivir en España sin trabajar', href: '/residencia-no-lucrativa' },
      { label: 'Profesionales cualificados', description: 'Visado para perfiles altamente cualificados', href: '/visado-para-profesionales-altamente-cualificados' },
    ],
    servicesAriaLabel: 'Servicios principales',
    breakingBadge: 'Novedad',
    breakingHeadline: 'Nómada digital + Ley Beckham: visado y fiscalidad en un solo despacho',
    headingLine1: 'Abogados de extranjería en España:',
    headingLine2: 'visados, nómada digital, residencia y fiscalidad',
    intro:
      'Revisamos tu caso, preparamos el expediente y lo presentamos por ti. Un abogado colegiado te acompaña en todo el proceso:',
    bullets: [
      'Sin colas ni desplazamientos: gestionamos tu trámite 100% online.',
      'Un abogado experto en extranjería revisa tu caso antes de presentar nada.',
      'Presupuesto claro y honorarios cerrados desde el primer contacto.',
    ],
    trustBadges: [
      '✅ Colegiados Certificados',
      'Respuesta en menos de 24h',
      '+500 casos resueltos',
    ],
    satisfactionLabel: 'Satisfacción de clientes',
    reviewsWord: 'opiniones',
    primaryCta: 'Trámites disponibles',
    secondaryCta: 'Habla con un especialista',
    aiCta: '¿No sabes qué trámite te corresponde? Usa el recomendador',
    asesoriaCta: 'Asesoría legal con un abogado — Respuesta en menos de 24 horas',
    footnote: 'Consulta inicial gratuita · Sin compromiso · Confidencialidad',
  },
  en: {
    services: [
      { label: 'Digital nomad visa', description: 'Work remotely from Spain', href: '/en/visado-nomada-digital' },
      { label: 'Beckham Law', description: 'Pay a flat 24% as an impatriate', href: '/en/ley-beckham' },
      { label: 'Non-lucrative residence', description: 'Live in Spain without working', href: '/en/residencia-no-lucrativa' },
      { label: 'Highly qualified professionals', description: 'Visa for highly skilled profiles', href: '/en/visado-para-profesionales-altamente-cualificados' },
    ],
    servicesAriaLabel: 'Main services',
    breakingBadge: 'New',
    breakingHeadline: 'Digital nomad + Beckham Law: visa and tax under one roof',
    headingLine1: 'Spanish immigration lawyers:',
    headingLine2: 'digital nomad visa, residency and Beckham Law',
    intro:
      'We review your case, prepare your file and submit it for you. A registered lawyer supports you throughout the process:',
    bullets: [
      'No queues or travel: we handle your procedure 100% online.',
      'An experienced immigration lawyer reviews your case before anything is filed.',
      'Clear quote and fixed fees from the very first contact.',
    ],
    trustBadges: [
      '✅ Bar-registered lawyers',
      'Reply in under 24h',
      '+500 successful cases',
    ],
    satisfactionLabel: 'Client satisfaction',
    reviewsWord: 'reviews',
    primaryCta: 'Available procedures',
    secondaryCta: 'Talk to a specialist',
    aiCta: "Not sure which procedure fits you? Use the recommender",
    asesoriaCta: 'Legal advice with a lawyer — Reply in under 24h',
    footnote: 'Free initial consultation · No commitment · Confidential',
  },
}

export default function Hero({ locale = 'es' }: { locale?: HeroLocale }) {
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const copy = HERO_COPY[locale]
  const localize = (href: string) => localizeInternalHref(href, locale)
  const trustpilotScore = testimonialsData.trustpilotScore
  const trustpilotReviews = testimonialsData.trustpilotReviews
  const trustpilotScoreLabel = trustpilotScore.toFixed(1).replace('.', locale === 'es' ? ',' : '.')

  return (
    <section className="hero-section" itemScope itemType="https://schema.org/Service">
      {modalIsOpen ? (
        <FormModal isOpen={modalIsOpen} onRequestClose={() => setModalIsOpen(false)} />
      ) : null}

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
        <div
          className="pointer-events-none absolute bottom-8 left-0 -z-10 hidden -translate-x-8 lg:block"
          aria-hidden="true"
        >
          <svg width="240" height="240" viewBox="0 0 240 240" fill="none">
            <circle cx="120" cy="120" r="100" fill="url(#heroGlow)" fillOpacity="0.35" />
            <circle cx="70" cy="70" r="34" fill="#FFC674" fillOpacity="0.22" />
            <defs>
              <radialGradient id="heroGlow" cx="0" cy="0" r="1" gradientTransform="translate(120 120) rotate(90) scale(110)">
                <stop stopColor="#FFC674" />
                <stop offset="1" stopColor="#FFC674" stopOpacity="0" />
              </radialGradient>
            </defs>
          </svg>
        </div>

        <div className="relative pb-10 pt-10 md:pb-16 md:pt-16">
          <div className="mx-auto max-w-3xl pb-12 text-center md:pb-16">
            <h1 className="h1 hero-title mb-4 lg:hero-title" itemProp="name">
              <span className="block text-gray-100 mb-2">{copy.headingLine1}</span>
              <span className="block text-amber-300 drop-shadow-xl font-black uppercase tracking-tight">
                {copy.headingLine2}
              </span>
            </h1>

            {/* Accesos directos a los servicios principales (alineados con las campañas de Google Ads) */}
            <nav aria-label={copy.servicesAriaLabel} className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
              {copy.services.map((service) => (
                <a
                  key={service.href}
                  href={service.href}
                  className="group rounded-xl border border-amber-400/30 bg-amber-400/5 px-3 py-3 text-left transition-all hover:border-amber-300/70 hover:bg-amber-400/10"
                >
                  <span className="block text-sm font-bold text-amber-200 group-hover:text-amber-100 sm:text-base">
                    {service.label} <span aria-hidden="true">→</span>
                  </span>
                  <span className="mt-1 block text-xs text-gray-400">{service.description}</span>
                </a>
              ))}
            </nav>

            {/* Aviso secundario - Nómada digital + Ley Beckham */}
            <a
              href={localize('/ley-beckham')}
              className="mb-8 inline-flex items-center gap-2 rounded-full border border-red-400/30 bg-red-500/10 px-4 py-1.5 text-xs text-red-100 transition-colors hover:border-red-300/60 sm:text-sm"
            >
              <span className="rounded bg-red-500/80 px-1.5 py-0.5 text-[10px] font-black uppercase text-white">
                {copy.breakingBadge}
              </span>
              <span>{copy.breakingHeadline}</span>
              <span aria-hidden="true">→</span>
            </a>

            <div className="mb-8 text-base md:text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed text-left bg-black/20 p-6 rounded-2xl border border-gray-700/50" itemProp="description">
              <p className="mb-5 text-center text-gray-200">
                {copy.intro}
              </p>
              <ul className="space-y-4 font-medium text-sm md:text-base">
                <li className="flex items-start gap-3">
                  <span className="text-xl shrink-0">⏳</span> 
                  <span>{copy.bullets[0]}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-xl shrink-0">🤝</span> 
                  <span>{copy.bullets[1]}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-xl shrink-0">⚖️</span> 
                  <span>{copy.bullets[2]}</span>
                </li>
              </ul>
            </div>

            {/* Badges de Confianza y Herramientas IA */}
            <div className="mb-10 flex flex-wrap justify-center gap-3">
              <div className="rounded-full bg-white/5 border border-white/10 px-4 py-1.5 text-xs sm:text-sm font-medium text-gray-300">
                {copy.trustBadges[0]}
              </div>

              <a
                className="rounded-full bg-amber-500/10 border border-amber-500/20 px-4 py-1.5 text-xs sm:text-sm font-bold text-amber-300 hover:bg-amber-500/20 transition-all flex items-center gap-2"
                href={localize('/comprueba-tu-tramite-ia')}
              >
                <span className="flex h-2 w-2 rounded-full bg-amber-400 animate-pulse"></span>
                {copy.trustBadges[1]}
              </a>
              <a
                className="rounded-full border border-[#00b67a]/40 bg-[#00b67a]/15 px-4 py-1.5 text-xs sm:text-sm font-bold text-emerald-100 shadow-[0_0_0_1px_rgba(0,182,122,0.12)] transition-all hover:border-[#00b67a]/70 hover:bg-[#00b67a]/25"
                href={testimonialsData.trustpilotProfileUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${trustpilotScoreLabel}/5 · ${trustpilotReviews} ${copy.reviewsWord} en Trustpilot`}
              >
                <span className="inline-flex items-center gap-2">
                  <span className="inline-flex rounded-sm bg-[#00b67a] px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-white">
                    Trustpilot
                  </span>
                  <span className="text-white">{trustpilotScoreLabel}/5</span>
                  <span className="text-emerald-100/80">{copy.satisfactionLabel}</span>
                  <span className="hidden text-emerald-100/60 sm:inline">
                    · {trustpilotReviews} {copy.reviewsWord}
                  </span>
                </span>
              </a>
              <div className="rounded-full bg-white/5 border border-white/10 px-4 py-1.5 text-xs sm:text-sm font-medium text-gray-300">
                {copy.trustBadges[2]}
              </div>
            </div>

            {/* Botones de Acción Estilo Landing */}
            <div className="mx-auto max-w-xs sm:flex sm:max-w-none sm:justify-center gap-4 flex-wrap">
              <div className="w-full sm:w-auto">
                <a
                  className="inline-flex items-center justify-center w-full px-8 py-4 text-base font-black text-gray-900 bg-amber-400 border border-amber-300 rounded-xl hover:bg-amber-300 hover:scale-[1.02] transition-all duration-300 shadow-[0_10px_20px_rgba(251,191,36,0.2)]"
                  href={localize('/empieza-tu-tramite')}
                >
                  {copy.primaryCta}
                </a>
              </div>
              <div className="w-full sm:w-auto mt-4 sm:mt-0">
                <button
                  className="inline-flex items-center justify-center w-full px-8 py-4 text-base font-bold text-white bg-white/10 backdrop-blur-md border border-white/20 rounded-xl hover:bg-white/20 hover:border-white/30 transition-all duration-300 shadow-lg"
                  onClick={() => setModalIsOpen(true)}
                >
                  {copy.secondaryCta}
                </button>
              </div>
            </div>

            {/* CTA destacado IA — para quienes no saben a qué trámite pueden optar */}
            <div className="mx-auto mt-5 max-w-xl">
              <a
                className="group inline-flex w-full items-center justify-center gap-3 rounded-xl border border-blue-400/30 bg-gradient-to-r from-blue-600/20 to-indigo-600/20 px-6 py-4 text-base font-bold text-blue-100 backdrop-blur-md transition-all duration-300 hover:from-blue-600/30 hover:to-indigo-600/30 hover:border-blue-400/50 hover:scale-[1.01]"
                href={localize('/comprueba-tu-tramite-ia')}
              >
                <span className="text-2xl">🤖</span>
                <span>{copy.aiCta}</span>
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </a>
            </div>

            {/* CTA Asesoría legal — respuesta en menos de 24 horas */}
            <div className="mx-auto mt-4 max-w-xl">
              <a
                className="group inline-flex w-full items-center justify-center gap-3 rounded-xl border border-emerald-400/30 bg-gradient-to-r from-emerald-600/20 to-teal-600/20 px-6 py-4 text-base font-bold text-emerald-100 backdrop-blur-md transition-all duration-300 hover:from-emerald-600/30 hover:to-teal-600/30 hover:border-emerald-400/50 hover:scale-[1.01]"
                href={localize('/asesoria-legal')}
              >
                <span className="text-2xl">⚖️</span>
                <span>{copy.asesoriaCta}</span>
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </a>
            </div>

            <p className="mt-8 text-xs sm:text-sm text-gray-400 font-medium">
              {copy.footnote}
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
